import React, { FC } from 'react';
import { Article, Territory } from 't2-graphql-types';
import composeFp from 'lodash/fp/compose';
import { NextSeo } from 'next-seo';
import { APP_URL } from 'config';
import { PromptPageContextProvider } from 'src/components/Prompt/context/PromptPageContext';
import { PromptReplyPreview } from 'src/components/PromptReplyPreview/PromptReplyPreview';
import { AsidePanelContextProvider } from 'src/components/AsidePanel/contexts/AsidePanelContext';
import { BreadcrumbsListContainer } from 'src/components/Breadcrumbs/BreadcrumbsListContainer';
import { useBreadcrumbHeight } from 'src/components/Breadcrumbs/hooks/useBreadcrumbHeight';
import Container from '../../../components/Container/Container';
import Navbar from '../../../components/Navbar';
import { withApolloData } from '../../../services/apolloClient/withApolloData';
import { getApolloServerClient, getCookiesForApolloServerClient } from '../../../services/apolloClient/server';
import { getPromptBySlugQuery } from '../../../components/Prompt/queries/getPromptBySlug';
import { useNavbarHeight } from '../../../components/Navbar/hooks/useNavbarHeight';
import { behindFeatureFlag } from '../../../components/FeatureFlags/getServerSidePropsDecorators/behindFeatureFlag';
import CommentsContainer from '../../../components/CommentsSection/CommentsContainer';
import { getFeatureFlag } from '../../../components/FeatureFlags/getFeatureFlag';
import { PromptArticlesSectionContainer } from '../../../components/PromptArticlesSectionContainer/PromptArticlesSectionContainer';
import PromptHeaderContainer from '../../../components/PromptHeader/PromptHeaderContainer';
import HorizontalSeparator from '../../../components/HorizontalSeparator/HorizontalSeparator';
import { logVisit } from '../../../services/visits/logVisit';

type TPromptPageProps = {
  promptId: Article['id'];
  territoryId: Territory['id'];
  title: Article['title'];
  shortDescription: Article['shortDescription'];
  articleLink: Article['articleLink'];
  coverImage: Article['coverImage'];
};

const PromptPage: FC<TPromptPageProps> = ({
  promptId,
  articleLink,
  title,
  shortDescription,
  coverImage,
  territoryId,
}) => {
  const navbarHeight = useNavbarHeight();
  const breadcrumbsHeight = useBreadcrumbHeight();
  const showCommentsSection = getFeatureFlag('commentsSection');

  return (
    <PromptPageContextProvider promptId={promptId} territoryId={territoryId}>
      <AsidePanelContextProvider>
        <div className="min-h-fitScreen">
          <NextSeo
            title={title || undefined}
            description={shortDescription || undefined}
            openGraph={{
              type: 'article',
              url: articleLink || undefined,
              title: title || '',
              description: shortDescription || undefined,
              images: [
                {
                  url: coverImage?.publicUrl || `${APP_URL}/images/prompt-page-opengraph-image.png`,
                },
              ],
            }}
          />
          <Navbar themeVariant="light" />
          <Container>
            <div className="col-span-full self-start">
              <BreadcrumbsListContainer articleId={promptId} type="prompt" />
            </div>
          </Container>
          <Container
            className="relative md:grid-cols-12 md:gap-x-3"
            style={{ paddingTop: navbarHeight + breadcrumbsHeight }}
          >
            <PromptHeaderContainer promptId={promptId} />
            <div className="my-10 md:my-12 lg:my-16 col-span-full lg:col-start-2 lg:col-span-10 order-3">
              <HorizontalSeparator />
            </div>
            <PromptArticlesSectionContainer promptId={promptId} />
            <div className="my-10 md:my-12 lg:my-16 col-span-full lg:col-start-2 lg:col-span-10 order-5">
              <HorizontalSeparator />
            </div>
            {showCommentsSection && (
              <div className="col-span-full md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4 order-6">
                <CommentsContainer articleId={promptId} />
              </div>
            )}
          </Container>
        </div>
        <PromptReplyPreview />
      </AsidePanelContextProvider>
    </PromptPageContextProvider>
  );
};

export default PromptPage;

export const getServerSideProps = composeFp(
  behindFeatureFlag('prompts'),
  withApolloData(PromptPage),
  logVisit,
)(async ({ req, query }) => {
  const apolloClient = getApolloServerClient({
    cookie: getCookiesForApolloServerClient(req.headers.cookie),
  });
  const { promptSlug } = query || {};

  const promptResponse = await apolloClient.query<{ article: Article & { territory: Pick<Territory, 'id'> } }>({
    query: getPromptBySlugQuery,
    variables: {
      promptSlug,
    },
  });

  const {
    article: { id: promptId, territory, ...promptData },
  } = { ...(promptResponse?.data || { territory: null, id: null }) };
  const territoryId = territory?.id;
  const isPrompt = promptData.type === 'PROMPT';
  const isPublished = promptData.status === 'published';
  const isDeactivated = promptData.deactivated === true;

  if (!promptId || !isPrompt || !isPublished || isDeactivated) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      promptId,
      territoryId,
      ...promptData,
    },
  };
});
